import { z } from "zod";
import { pageReadingSchema, siteCategorySchema, siteKindSchema, } from "./off-page-check.js";
import { isoDateSchema as isoDate, reportFilterSchema } from "./report-filter.js";
/**
 * Off-page: the other websites AI answers lean on, and where you are missing.
 *
 * Everything in this file is read from captures we already hold — citations
 * and the brands each answer named. No page is fetched and no model is asked,
 * so every number here is observed, and the screen can open on it for free.
 *
 * A site is "off-page" when a report's answers cited it and it is neither the
 * subject's own domain nor a tracked competitor's. The list says nothing yet
 * about what kind of site it is — that is a separate, evidenced step, and a
 * column of guesses would be worse than no column (AGENTS.md §1).
 */
/** How many rows one request may ask for. Every list here pages on the server. */
const MAX_PAGE_SIZE = 100;
export const offPagePagingSchema = z.object({
    limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
export const offPageSitesFilterSchema = reportFilterSchema.extend(offPagePagingSchema.shape);
/**
 * Which sites to list.
 *
 * `publishers` (the default) is only sites confirmed as a publishing kind — news,
 * niche publications, editorial and publishing platforms, blogs — by evidence or
 * by the workspace, and not hidden. `undecided` is the sites with no confirmed
 * kind yet, including ones that block our reader, so none disappear unseen.
 * `all` is everything, hidden sites included.
 */
export const offPageSiteListSchema = offPageSitesFilterSchema.extend({
    /**
     * `usable` (what Where to publish shows) is every site that is a publishing
     * kind **or** listed in the outreach team's OMS inventory — somewhere a post
     * could go, by its kind or because it can be bought.
     */
    sites: z.enum(["usable", "publishers", "undecided", "all"]).default("publishers"),
    /** Part of a domain, to find one site in a long list. Matched case-blind, anywhere. */
    q: z.string().trim().min(1).max(100).optional(),
    /** One kind of site. Narrows the list; never the summary above it. */
    category: siteCategorySchema.optional(),
    /**
     * Narrowing and order by OMS's own figures (agreed 2026-10-05). OMS is not
     * in our database, so these are applied by the service over the sites it
     * reads, and only when OMS answers; the response's `oms` says when it did not.
     * A site with no figure never passes a bound on it.
     */
    inOms: z.literal("1").optional(),
    minDa: z.coerce.number().int().min(0).max(1000).optional(),
    minTraffic: z.coerce.number().int().min(0).optional(),
    maxSpam: z.coerce.number().int().min(0).max(100).optional(),
    maxPrice: z.coerce.number().int().min(0).optional(),
    /** `gap` is the list's own order; the rest order by an OMS figure. */
    sort: z.enum(["gap", "da", "traffic", "organic", "price"]).default("gap"),
    /** Absent means each figure's natural way: most DA and traffic, cheapest price. */
    dir: z.enum(["asc", "desc"]).optional(),
});
const brandRefSchema = z.object({ brandId: z.string(), brandName: z.string() });
export const offPageSiteRowSchema = z.object({
    domain: z.string(),
    kind: siteKindSchema,
    /** Distinct answers that cited this site at least once. The denominator below. */
    answers: z.number().int().min(1),
    pages: z.number().int().min(1),
    prompts: z.number().int().min(1),
    /** Of `answers`, how many named the subject. */
    subjectNamed: z.number().int().min(0),
    /**
     * The tracked competitor named in the most of those answers.
     *
     * Null when no tracked competitor was named in any of them — which is a
     * finding ("nobody you track shows up here"), not a missing value.
     */
    leader: brandRefSchema.extend({ named: z.number().int().min(1) }).nullable(),
});
export const offPageSitesSchema = z.object({
    reportId: z.string(),
    brandId: z.string(),
    brandName: z.string(),
    from: isoDate,
    to: isoDate,
    /** Every off-page site in scope that passes the `sites` filter, before paging. */
    total: z.number().int().min(0),
    /**
     * Every brand this report tracks, by id, with its primary domain — so the
     * screen can draw a logo for any brand named anywhere in this payload.
     */
    brandDomains: z.record(z.string(), z.string()),
    /** Sites in scope with no confirmed kind, whatever the filter — so the list can say how many are waiting. */
    undecided: z.number().int().min(0),
    /**
     * The figures over the list, counted over every site the `sites` view admits
     * in range — not the search or kind narrowing it — so they stay still while a
     * reader looks for one site. For `usable`, over every cited site (its "Show
     * all" count), hidden ones included.
     */
    summary: z.object({
        /** Sites in the view. */
        sites: z.number().int().min(0),
        /** Distinct answers that cited at least one of them. */
        answers: z.number().int().min(0),
        /** Sites whose answers named a tracked competitor and never you. */
        competitorOnly: z.number().int().min(0),
        /** Sites whose top competitor is named in more of their answers than you are. */
        trailing: z.number().int().min(0),
    }),
    /**
     * How OMS shaped this list. `checked` — the `usable` view asked OMS about
     * every non-publishing site. `off` — no OMS here, so it is publishers only.
     * `unavailable` — OMS did not answer, so it is publishers only, said rather
     * than shown as if nothing were listed. `too_many` — more cited sites than
     * one request may check, so it is publishers only and says to narrow the
     * dates. `not_asked` — any other view.
     */
    oms: z.enum(["checked", "off", "unavailable", "too_many", "not_asked"]),
    /** Largest gap to the leading competitor first, then most answers. */
    sites: z.array(offPageSiteRowSchema),
});
export const offPageSiteFilterSchema = offPageSitesFilterSchema.extend({
    domain: z.string().trim().min(1).max(253),
});
export const offPagePageRowSchema = z.object({
    url: z.string(),
    /** Whatever the engine labelled the link. Null stays null. */
    title: z.string().nullable(),
    answers: z.number().int().min(1),
    prompts: z.number().int().min(1),
    subjectNamed: z.number().int().min(0),
});
export const offPageSiteDetailSchema = z.object({
    domain: z.string(),
    kind: siteKindSchema,
    brandId: z.string(),
    brandName: z.string(),
    /**
     * Every brand this report tracks, by id, with its primary domain — so the
     * screen can draw a logo for any brand named anywhere in this payload.
     */
    brandDomains: z.record(z.string(), z.string()),
    answers: z.number().int().min(1),
    /**
     * The subject, then every tracked competitor named at least once in these
     * answers, most-named first. The subject is always present, even at zero —
     * "you are named in none of them" is the headline, not an omission.
     */
    brands: z.array(brandRefSchema.extend({
        isSubject: z.boolean(),
        named: z.number().int().min(0),
    })),
    totalPages: z.number().int().min(1),
    /** Pages where you are named least often first — the place to start. */
    pages: z.array(offPagePageRowSchema),
});
/**
 * Which answers to list for one page.
 *
 * `missing` is the answers that did not name the subject — the ones worth a
 * look first. It is a filter on stored mentions, not a judgement.
 */
export const offPageAnswersShowSchema = z.enum(["missing", "all"]);
export const offPagePageFilterSchema = offPageSitesFilterSchema.extend({
    url: z.url().max(2048),
    show: offPageAnswersShowSchema.default("all"),
});
/**
 * Where the link sits in the answer.
 *
 * `located` is the sentence the source label is printed straight after.
 * `unplaced` carries why we cannot say — Google AI Overviews keep their links
 * in a rail with no position in the text, and some older captures predate the
 * position being stored.
 */
export const citedTextSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("located"), sentence: z.string().min(1) }),
    z.object({
        kind: z.literal("unplaced"),
        reason: z.enum(["no_position", "position_out_of_range", "no_text_before_link"]),
    }),
]);
export const offPageAnswerSchema = z.object({
    runId: z.string(),
    engine: z.string(),
    runDate: isoDate,
    promptId: z.string(),
    promptText: z.string(),
    namesSubject: z.boolean(),
    /** Tracked brands (subject included) this answer named. */
    brands: z.array(brandRefSchema),
    citedText: citedTextSchema,
});
export const offPagePageSchema = z.object({
    url: z.string(),
    domain: z.string(),
    title: z.string().nullable(),
    brandId: z.string(),
    brandName: z.string(),
    /**
     * Every brand this report tracks, by id, with its primary domain — so the
     * screen can draw a logo for any brand named anywhere in this payload.
     */
    brandDomains: z.record(z.string(), z.string()),
    /** Every answer in scope citing this exact page, whatever `show` says. */
    answers: z.number().int().min(1),
    subjectNamed: z.number().int().min(0),
    /** Rows matching `show`, before paging. */
    matching: z.number().int().min(0),
    /** The newest time this page was read by a page check, or null if never. */
    reading: pageReadingSchema.nullable(),
    rows: z.array(offPageAnswerSchema),
});
export const offPageSiteAnswersFilterSchema = offPageSitesFilterSchema.extend({
    domain: z.string().trim().min(1).max(253),
    show: offPageAnswersShowSchema.default("all"),
});
/**
 * Every answer that cited one site, newest first — a site's "Responses" tab.
 *
 * One row per answer, however many of the site's pages it cited: `pages` lists
 * them, earliest-cited first, and `citedText` is the sentence the earliest one
 * sits behind, which is what the answer opens with marked.
 */
export const offPageSiteAnswersSchema = z.object({
    domain: z.string(),
    brandId: z.string(),
    brandName: z.string(),
    brandDomains: z.record(z.string(), z.string()),
    /** Every answer in scope citing this site, whatever `show` says. */
    answers: z.number().int().min(1),
    subjectNamed: z.number().int().min(0),
    /** Rows matching `show`, before paging. */
    matching: z.number().int().min(0),
    rows: z.array(offPageAnswerSchema.extend({
        pages: z.array(z.object({ url: z.string(), title: z.string().nullable() })).min(1),
    })),
});
//# sourceMappingURL=off-page.js.map